import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { toast } from "react-toastify";
import { Mutex } from "async-mutex";
import { apiBaseUrl } from "../components/consts/api-url.const";
import { showSpotifyReconnect } from "../store/popupSlice";

const mutex = new Mutex();
const baseUrl = `${apiBaseUrl}`;

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = localStorage.getItem("token");
    const metaAdsPublicToken = localStorage.getItem("metaAdsPublicToken");

    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (metaAdsPublicToken) headers.set("x-auth-token", metaAdsPublicToken);

    return headers;
  },
});

export const fetchbase: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  const errorData = result?.error?.data as
    | { requireSpotifyReconnect?: boolean; requireMetaAdsLogin?: boolean; detail?: any }
    | undefined;

  if (errorData?.requireSpotifyReconnect) {
    const refreshToken = localStorage.getItem("refresh_token");

    if (refreshToken) {
      const release = await mutex.acquire();
      try {
        const refreshResponse = await fetch(
          `${baseUrl}/api/auth/refresh_token?refresh_token=${refreshToken}`
        );
        if (!refreshResponse.ok) throw new Error("Failed to refresh token");

        const refreshData = await refreshResponse.json();
        const newToken = refreshData?.access_token;

        if (newToken) {
          localStorage.setItem("token", newToken);
          result = await baseQuery(args, api, extraOptions);
        }
      } catch (err) {
        console.error("Token refresh failed:", err);
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        api.dispatch(showSpotifyReconnect());
      } finally {
        release();
      }
    } else {
      // Clear Spotify tokens when no refresh token is available
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      api.dispatch(showSpotifyReconnect());
    }
    return result;
  }

  if (errorData?.requireMetaAdsLogin) {
    const metaToken = localStorage.getItem("metaAdsPublicToken");
    if (metaToken) {
      fetch(`${baseUrl}/api/meta-builder/auth/disconnect`, {
        method: "POST",
        headers: { "x-auth-token": metaToken },
      }).catch(() => {});
    }
    clearMetaAdsAuthFromStorage();
    window.location.href = "/meta-ads-login";
    return result;
  }

  if (result?.error && result.error.status === 401) {
    const refreshToken = localStorage.getItem("refresh_token");

    if (refreshToken) {
      const release = await mutex.acquire();
      try {
        const refreshResponse = await fetch(
          `${baseUrl}/api/auth/refresh_token?refresh_token=${refreshToken}`
        );
        if (!refreshResponse.ok) throw new Error("Failed to refresh token");

        const refreshData = await refreshResponse.json();
        const newToken = refreshData?.access_token;

        if (newToken) {
          localStorage.setItem("token", newToken);
          result = await baseQuery(args, api, extraOptions);
        }
      } catch (err) {
        console.error("Token refresh failed:", err);
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        api.dispatch(showSpotifyReconnect());
      } finally {
        release();
      }
    } else {
      // Clear Spotify tokens when no refresh token is available
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      api.dispatch(showSpotifyReconnect());
    }
  } else if (result?.error?.data) {
    // Don't show toast for banned artist errors - they're handled specifically in components
    const errorData = result.error.data as any;
    if (errorData?.isBanned !== true && errorData?.isBanned !== "true") {
      const msg = getFirstErrorFromObject(result.error.data);
      if (msg) toast.error(msg);
    }
  }

  return result;
};

function getFirstErrorFromObject(obj: any) {
  if (obj?.detail) {
    if (Array.isArray(obj.detail)) {
      const eItem = obj.detail[0];
      if (eItem) {
        return `${eItem.msg}`;
      }
    } else if (obj.detail) {
      return obj.detail.toString();
    }
  }
}

// Meta Ads public token expiry (so expired token is removed from localStorage)
const META_ADS_EXPIRY_BUFFER_MS = 60 * 1000; // 1 minute

export function clearMetaAdsAuthFromStorage(): void {
  localStorage.removeItem("metaAdsPublicToken");
  localStorage.removeItem("metaAdsUserEmail");
  localStorage.removeItem("metaAdsUserId");
  localStorage.removeItem("metaAdsUserName");
  localStorage.removeItem("metaAdsTokenExpiresAt");
  sessionStorage.removeItem("metaAdsSelections");
  sessionStorage.removeItem("metaAdsSelectedPixel");
  sessionStorage.removeItem("metaAdsSelectedPage");
  sessionStorage.removeItem("metaAdsSelectedInstagram");
  sessionStorage.removeItem("metaAdsSelectedAdAccount");
  sessionStorage.removeItem("facebookUserId");
  localStorage.removeItem("metaAdsAccountData");
}

/** Call disconnect API with token then clear storage. Used when token is expired so Facebook is disconnected. */
function disconnectMetaAdsThenClear(token: string): void {
  fetch(`${baseUrl}/api/meta-builder/auth/disconnect`, {
    method: "POST",
    headers: { "x-auth-token": token },
  }).catch(() => {});
  clearMetaAdsAuthFromStorage();
}

function getJwtExpFromToken(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload)) as { exp?: number };
    return typeof decoded.exp === "number" ? decoded.exp : null;
  } catch {
    return null;
  }
}

/** Returns true if Meta Ads token is missing or expired (with 1min buffer). Clears storage if expired. */
export function isMetaAdsTokenExpired(): boolean {
  const token = localStorage.getItem("metaAdsPublicToken");
  if (!token) return true;

  const storedExpiresAt = localStorage.getItem("metaAdsTokenExpiresAt");
  const now = Date.now() + META_ADS_EXPIRY_BUFFER_MS;

  if (storedExpiresAt) {
    const expiresAtMs = new Date(storedExpiresAt).getTime();
    if (now >= expiresAtMs) {
      disconnectMetaAdsThenClear(token);
      return true;
    }
    return false;
  }

  const exp = getJwtExpFromToken(token);
  if (exp !== null) {
    const expiresAtMs = exp * 1000;
    if (now >= expiresAtMs) {
      disconnectMetaAdsThenClear(token);
      return true;
    }
    return false;
  }

  return false;
}
